"use client";

import React, { useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            obs.unobserve(e.target);
          }
        }),
      { threshold: 0.06 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

interface Article {
  id: number;
  title: string;
  date: string;
  category: string;
  snippet: string;
  imageAlt: string;
  imageBg: string;
  href: string;
}

const articles: Article[] = [
  {
    id: 1,
    title: 'The Structural Case for Dividend Growth in a Higher-for-Longer Rate Environment',
    date: 'April 24, 2026',
    category: 'Dividend Equities',
    snippet:
      'As central banks signal a prolonged restrictive stance, free cash flow discipline separates durable dividend growers from yield traps. We examine the FCF coverage ratios across 40 S&P constituents.',
    imageAlt: 'Aerial view of global financial district skyline at dusk with illuminated trading floors',
    imageBg: 'linear-gradient(135deg, #0F2A44 0%, #1A3A5C 40%, #0A2540 100%)',
    href: '#',
  },
  {
    id: 2,
    title: 'EUR/USD Macro Regime Shift: ECB Divergence and the Carry Trade Unwind',
    date: 'April 18, 2026',
    category: 'Forex',
    snippet:
      'The ECB-Fed rate differential is compressing faster than consensus expects. Swap market positioning reveals extreme EUR short crowding — a setup historically preceding sharp mean-reversion moves.',
    imageAlt: 'Close-up of currency exchange rate boards in a European financial center',
    imageBg: 'linear-gradient(135deg, #0A2540 0%, #0F3A5A 50%, #1A2A44 100%)',
    href: '#',
  },
  {
    id: 3,
    title: 'Bitcoin Post-Halving Supply Dynamics: On-Chain Evidence of Institutional Accumulation',
    date: 'April 11, 2026',
    category: 'Digital Assets',
    snippet:
      'Whale cohort data (1k–10k BTC wallets) shows net accumulation of 38,000 BTC over 30 days post-halving. Exchange outflow velocity has reached a 14-month high, signalling structural demand absorption.',
    imageAlt: 'Abstract visualization of blockchain network nodes and digital asset flows',
    imageBg: 'linear-gradient(135deg, #0D2540 0%, #1A3A5C 40%, #0A2030 100%)',
    href: '#',
  },
  {
    id: 4,
    title: 'Realty Income and the REIT Revaluation Thesis: Yield Compression Ahead',
    date: 'April 4, 2026',
    category: 'REIT / Income',
    snippet:
      'UK and US REIT sectors are pricing in excessive rate risk relative to forward BoE and Fed guidance. Our DCF model identifies a 15–22% revaluation gap in diversified REITs with 5%+ distribution yields.',
    imageAlt: 'Wide-angle photograph of commercial real estate portfolio including office towers and retail centers',
    imageBg: 'linear-gradient(135deg, #0A2540 0%, #0F3C6A 55%, #1A3050 100%)',
    href: '#',
  },
  {
    id: 5,
    title: 'BoJ Normalization Risk: Asymmetric JPY Reversal and the Carry Trade Unwind',
    date: 'March 28, 2026',
    category: 'Forex',
    snippet:
      'Interbank positioning data shows extreme short-JPY crowding not seen since 2007. Any BoJ acceleration signal could trigger a rapid unwind — asymmetric risk/reward favours reducing JPY carry exposure now.',
    imageAlt: 'Tokyo financial district at night with Bank of Japan headquarters in foreground',
    imageBg: 'linear-gradient(135deg, #0F2540 0%, #1A3A5C 40%, #0A1E30 100%)',
    href: '#',
  },
  {
    id: 6,
    title: 'Semiconductor Dividend Growers: AI Capex Cycle and FCF Expansion',
    date: 'March 21, 2026',
    category: 'Dividend Equities',
    snippet:
      'The AI infrastructure buildout is generating outsized FCF expansion in select semiconductor names. AVGO, TXN, and MSFT yield plays offer a rare combination of growth optionality and rising income.',
    imageAlt: 'Macro photograph of semiconductor wafer with circuit patterns under blue laboratory lighting',
    imageBg: 'linear-gradient(135deg, #0A2540 0%, #0F4070 50%, #1A2A44 100%)',
    href: '#',
  },
  {
    id: 7,
    title: 'Emerging Market Currency Basket: USD Peak and the Mean-Reversion Setup',
    date: 'March 14, 2026',
    category: 'Forex',
    snippet:
      'The USD strength cycle shows technical and fundamental signs of exhaustion. BRL, INR, and MXN carry setups offer risk-adjusted returns as the Fed pivot narrative solidifies into H2 2026 positioning.',
    imageAlt: 'Panoramic view of emerging market financial hubs including São Paulo and Mumbai skylines',
    imageBg: 'linear-gradient(135deg, #0D2540 0%, #1A3A5C 40%, #0A2030 100%)',
    href: '#',
  },
  {
    id: 8,
    title: 'Ethereum DeFi tvL Recovery: On-Chain Signals of Institutional Re-Engagement',
    date: 'March 7, 2026',
    category: 'Digital Assets',
    snippet:
      'Active validator count has reached an all-time high while DeFi tvL across major protocols has recovered 68% from cycle lows. Developer activity index is up 34% MoM — a leading indicator of sustained demand.',
    imageAlt: 'Digital art representation of Ethereum network with glowing nodes and smart contract flows',
    imageBg: 'linear-gradient(135deg, #0A2540 0%, #0F4070 50%, #1A2A44 100%)',
    href: '#',
  },
];

const categoryColor: Record<string, string> = {
  'Dividend Equities': '#A3D4FF',
  'Forex': '#A3D4FF',
  'Digital Assets': '#A3D4FF',
  'REIT / Income': '#A3D4FF',
};

function ArticleCard({ article, index }: { article: Article; index: number }) {
  return (
    <article
      className="reveal group flex flex-col sm:flex-row overflow-hidden rounded-2xl transition-all duration-300"
      style={{
        background: '#1A2A44',
        border: '1px solid #1E3A5F',
        transitionDelay: `${index * 60}ms`,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = 'rgba(163,212,255,0.35)';
        (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 40px rgba(74,144,226,0.12)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = '#1E3A5F';
        (e.currentTarget as HTMLElement).style.boxShadow = 'none';
      }}
    >
      {/* 16:9 Image Placeholder — left side */}
      <Link
        href={article.href}
        className="block flex-shrink-0 sm:w-72 md:w-80 lg:w-96 relative overflow-hidden"
        style={{ aspectRatio: '16/9' }}
        aria-label={article.imageAlt}
      >
        <div
          className="absolute inset-0 transition-transform duration-500 group-hover:scale-105"
          style={{ background: article.imageBg }}
        />
        {/* Subtle grid overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgba(163,212,255,0.04) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
          }}
        />
        {/* Category badge on image */}
        <div className="absolute bottom-3 left-3">
          <span
            className="text-xs font-mono font-bold px-2.5 py-1 rounded-md"
            style={{
              background: 'rgba(10,37,64,0.75)',
              color: '#A3D4FF',
              border: '1px solid rgba(163,212,255,0.2)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.1em',
              backdropFilter: 'blur(8px)',
            }}
          >
            {article.category}
          </span>
        </div>
        {/* Decorative chart line */}
        <svg
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-10"
          width="120"
          height="60"
          viewBox="0 0 120 60"
          fill="none"
        >
          <path
            d="M4 48L24 32L44 38L64 18L84 24L104 8L116 14"
            stroke="#A3D4FF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="64" cy="18" r="3" fill="#A3D4FF" />
        </svg>
      </Link>

      {/* Right side: content */}
      <div className="flex flex-col justify-center px-6 py-6 flex-1 min-w-0">
        {/* Date + Category row */}
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <span
            className="text-xs font-mono"
            style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em' }}
          >
            {article.date}
          </span>
          <span style={{ color: 'rgba(163,212,255,0.3)', fontSize: '10px' }}>•</span>
          <span
            className="text-xs font-mono font-semibold"
            style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em' }}
          >
            {article.category}
          </span>
        </div>

        {/* Title */}
        <Link href={article.href}>
          <h2
            className="font-display font-bold text-white leading-snug mb-3 transition-colors duration-200 group-hover:text-blue-200"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(1rem, 2vw, 1.15rem)',
              lineHeight: '1.4',
            }}
          >
            {article.title}
          </h2>
        </Link>

        {/* 2-line snippet */}
        <p
          className="text-sm leading-relaxed line-clamp-2"
          style={{ color: '#E3F2FD', opacity: 0.55, fontFamily: 'var(--font-body)' }}
        >
          {article.snippet}
        </p>

        {/* Read more link */}
        <Link
          href={article.href}
          className="inline-flex items-center gap-1.5 text-xs font-semibold mt-4 transition-opacity duration-200 hover:opacity-100"
          style={{ color: '#A3D4FF', opacity: 0.7, fontFamily: 'var(--font-body)', letterSpacing: '0.04em' }}
        >
          Read Analysis
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>
      </div>
    </article>
  );
}

export default function ResearchPage() {
  useReveal();

  return (
    <div style={{ background: '#0A2540', minHeight: '100vh' }}>
      <Header />

      {/* Dot-grid background */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(163,212,255,0.025) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          zIndex: 0,
        }}
      />

      <main className="relative z-10 pt-28 pb-24 px-4 sm:px-6 md:px-12" style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Page header */}
        <div className="reveal mb-12">
          <span
            className="text-xs font-mono uppercase tracking-widest px-3 py-1 rounded-full inline-block mb-5"
            style={{
              color: '#A3D4FF',
              background: 'rgba(163,212,255,0.08)',
              border: '1px solid rgba(163,212,255,0.2)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.18em',
            }}
          >
            Intelligence Feed
          </span>
          <h1
            className="font-display font-extrabold text-white mb-4 leading-tight"
            style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 2.75rem)' }}
          >
            Research Articles
          </h1>
          <p
            className="text-base leading-relaxed max-w-xl"
            style={{ color: '#E3F2FD', opacity: 0.55, fontFamily: 'var(--font-body)' }}
          >
            Macro-financial analysis across dividend equities, forex, and digital assets — published by the DividendChase research desk.
          </p>
        </div>

        {/* Filter bar */}
        <div className="reveal flex items-center gap-3 mb-10 flex-wrap">
          {['All', 'Dividend Equities', 'Forex', 'Digital Assets', 'REIT / Income'].map((cat, i) => (
            <button
              key={cat}
              className="text-xs font-mono px-4 py-1.5 rounded-full transition-all duration-200"
              style={{
                background: i === 0 ? 'rgba(163,212,255,0.12)' : 'transparent',
                color: i === 0 ? '#A3D4FF' : '#E3F2FD',
                border: i === 0 ? '1px solid rgba(163,212,255,0.3)' : '1px solid rgba(163,212,255,0.1)',
                opacity: i === 0 ? 1 : 0.55,
                fontFamily: 'var(--font-mono)',
                letterSpacing: '0.08em',
                cursor: 'pointer',
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Article feed */}
        <div className="flex flex-col gap-5">
          {articles.map((article, i) => (
            <ArticleCard key={article.id} article={article} index={i} />
          ))}
        </div>

        {/* Load more */}
        <div className="reveal flex justify-center mt-12">
          <button
            className="text-sm font-semibold px-8 py-3 rounded-full transition-all duration-200"
            style={{
              background: 'transparent',
              color: '#A3D4FF',
              border: '1px solid rgba(163,212,255,0.3)',
              fontFamily: 'var(--font-body)',
              letterSpacing: '0.04em',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(163,212,255,0.08)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            Load More Articles
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}
